import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { TextButton } from '@creditornot/cb-button';
import { Spinner } from '@creditornot/cb-progress';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { findDescendants } from 'common/utils/hierarchy';

import { Option } from './Option';
import { MultiSelectButtonsContainer, NoOptionsAvailable, StyledMenuItemGroup } from './styled';
import { TreeOptionChangeProps, TreeSelectOption } from './types';
import {
  initializeTreeOptions,
  getSearchResults,
  getCheckedOptionValues,
  getTreeValues,
} from './utils';
import { Stack } from '../Stack';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

type Props = {
  options: TreeSelectOption[];
  inputId?: string;
  onChange: (value: TreeOptionChangeProps) => void;
  multiselect?: boolean;
  search?: string;
  isLoading?: boolean;
  showSelectAll?: boolean;
  preserveChildSelections?: boolean;
};

export function TreeSelectOptions({
  options: _options,
  onChange,
  multiselect,
  search = '',
  isLoading,
  showSelectAll = true,
  preserveChildSelections = false,
}: Props) {
  const f = useFormatMessage();
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  const searchMode = Boolean(search);

  const options = useMemo(() => {
    const initializedOptions = initializeTreeOptions(_options);

    // When preserveChildSelections is true, ensure parent nodes are checked when all their leaf descendants are selected
    if (preserveChildSelections) {
      return initializedOptions.map((option) => {
        if (option.checked) {
          return option; // Already checked, no need to change
        }

        const descendants = findDescendants(option.value, initializedOptions);

        const leafDescendants = descendants.filter((desc) => {
          const childDescendants = findDescendants(desc.value, initializedOptions);
          return childDescendants.length === 0; // Only leaf nodes
        });

        // If this node has leaf descendants, check if all are selected
        if (leafDescendants.length > 0) {
          const allLeafDescendantsSelected = leafDescendants.every((desc) => desc.checked);

          return {
            ...option,
            checked: allLeafDescendantsSelected,
          };
        }

        return option;
      });
    }

    return initializedOptions;
  }, [_options, preserveChildSelections]);

  const searchResults = getSearchResults(search, options);

  const checkedOptions = options.filter(({ checked }) => checked);

  function handleOptionChange(currentOption: TreeSelectOption) {
    if (multiselect) {
      const isOptionChecked = !currentOption.checked;
      const option = { ...currentOption, checked: isOptionChecked };
      const checkedOptionValues = getCheckedOptionValues(option, options);

      // When preserveChildSelections is true, return all checked children instead of collapsing to parent
      let selectedIds = preserveChildSelections
        ? checkedOptionValues
        : getTreeValues(checkedOptionValues, options);

      // When preserveChildSelections is true, exclude parent items that have children
      if (preserveChildSelections) {
        selectedIds = selectedIds.filter((id) => {
          const descendants = findDescendants(id, options);
          return descendants.length === 0; // Only include items that have no children (leaf nodes)
        });
      }

      const unselectedIds = isAllSelected
        ? options.map((option) => option.value).filter((id) => !checkedOptionValues.includes(id))
        : [];

      onChange({ selectedIds, unselectedIds });
    } else {
      onChange({ selectedIds: [currentOption.value] });
    }
  }

  function changeAllOptions(checked: boolean) {
    setIsAllSelected(checked);

    const selectedIds = checked ? options.map((option) => option.value) : [];
    // When preserveChildSelections is true, return all selected IDs without collapsing to parents
    let finalSelectedIds = preserveChildSelections
      ? selectedIds
      : getTreeValues(selectedIds, options);

    // When preserveChildSelections is true, exclude parent items that have children
    if (preserveChildSelections && checked) {
      finalSelectedIds = finalSelectedIds.filter((id) => {
        const descendants = findDescendants(id, options);
        return descendants.length === 0; // Only include items that have no children (leaf nodes)
      });
    }

    onChange({ selectedIds: finalSelectedIds });
  }

  return (
    <Stack>
      <StyledMenuItemGroup data-test-id="hierarchy-select-item-group">
        {searchMode &&
          searchResults
            .filter((option) => !option.parentValue)
            .map((option) => {
              const descendants = findDescendants(option.value, searchResults);
              const searchedDescendants = descendants.filter((option) =>
                searchResults.some(({ value }) => value === option.value),
              );

              return (
                <Option
                  key={option.value}
                  option={option}
                  descendants={descendants}
                  expand={searchedDescendants.length > 0}
                  onChange={handleOptionChange}
                  multiselect={multiselect}
                />
              );
            })}
        {!searchMode &&
          options
            .filter((option) => !option.parentValue)
            .map((option) => {
              const descendants = findDescendants(option.value, options);
              return (
                <Option
                  option={option}
                  descendants={descendants}
                  key={option.value}
                  onChange={handleOptionChange}
                  multiselect={multiselect}
                />
              );
            })}
        {searchMode && !searchResults.length && (
          <NoOptionsAvailable>
            {f('components.tree_select.no_options_available')}
          </NoOptionsAvailable>
        )}
      </StyledMenuItemGroup>
      {multiselect && !!searchResults.length && showSelectAll && (
        <MultiSelectButtonsContainer>
          <TextButton
            variant="brand"
            onClick={() => changeAllOptions(true)}
            data-test-id="select-all-options"
          >
            {f('common.select_all')}
          </TextButton>
          {checkedOptions.length > 0 && (
            <TextButton
              variant="brand"
              onClick={() => changeAllOptions(false)}
              data-test-id="unselect-all-options"
            >
              {f('common.deselect_all')}
            </TextButton>
          )}
        </MultiSelectButtonsContainer>
      )}
      {isLoading && (
        <LoadingContainer>
          <Spinner size="small" />
        </LoadingContainer>
      )}
    </Stack>
  );
}
